<template>
  <div class="border-b px-4 py-8 md:px-8">
    <p class="text-[16px] md:text-[20px]">2. Select Preferred {{ isOvernightStayEvent ? 'Check-in' : '' }} Time</p>
    <!-- 凡例セクション -->
    <div class="flex justify-end pt-1">
      <div class="flex items-center gap-2 text-[12px] text-gray2 md:gap-4 md:text-[14px]">
        <div class="flex items-center gap-1">
          <span class="inline-flex h-[12px] w-[24px] items-center justify-center rounded-full border border-gray1"></span>
          <span>{{ availabilityStatusMap.CONFIRMED_INSTANTLY.label }}</span>
        </div>
        <span>|</span>
        <div class="flex items-center gap-1">
          <span class="inline-flex h-[12px] w-[24px] items-center justify-center rounded-sm border border-gray1"></span>
          <span>{{ availabilityStatusMap.ON_REQUEST.label }}</span>
        </div>
      </div>
    </div>
    <!-- 時間選択セクション -->
    <div class="mx-auto pt-2">
      <MoleculesFormInputWrapper v-if="isSelectedDate" data-testid="select-time-in-modal" label="" lang="en" :error="getErrorMessage('step1.experienceEventId')" :required="false">
        <div v-if="isLoading" class="flex flex-col gap-y-2" data-testid="datetime-options-skeleton">
          <MoleculesWabunkaSkeltonText class="h-4 w-[200px]" />
          <MoleculesWabunkaSkeltonText class="h-4 w-[200px]" />
        </div>
        <div v-else class="text-[16px] md:text-[17px]">
          <div v-if="canReserveEventOptions.length == 0" class="text-gray2">No Events</div>
          <div v-else>
            <button
              v-for="option in canReserveEventOptions"
              :key="option.value"
              aria-label="First preferred time options"
              role="radio"
              :aria-selected="option.value === reservationFormStep1.experienceEventId"
              :class="[
                'm-1 border-[0.5px] px-4 py-2 text-sm',
                option.reservationType === WabunkaExperienceEventReservationType.EXPERIENCE_EVENT_RESERVATION_TYPE_REQUEST ? 'rounded-[4px]' : 'rounded-full',
                option.value === reservationFormStep1.experienceEventId ? 'border-gold3 bg-beige1 text-gold1' : 'border-gray1 text-gray2',
              ]"
              @click="() => onChangeExperienceEventId(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
      </MoleculesFormInputWrapper>
      <p v-else class="ml-5 text-gray2" data-testid="no-selected-date-message-in-datetime">Please select a date first</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WabunkaExperienceEventReservationType } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_reservation_type';

import MoleculesFormInputWrapper from '@/components/molecules/FormInputWrapper/FormInputWrapper.vue';
import { availabilityStatusMap } from '@/models/wabunka/view/experience/wabunkaExperienceCalendar';
import { type WabunkaReservationFormStep1 } from '@/wabunka-src/features/Experience';
import {
  createDefaultWabunkaExperienceEventViewModel,
  type WabunkaCandidateEventOption,
  type WabunkaExperienceEventListViewModel,
  type WabunkaExperienceEventViewModel,
} from '@/wabunka-src/features/Experience/models/experienceEvent';

interface Props {
  isLoading?: boolean;
  reservationFormStep1: WabunkaReservationFormStep1;
  candidateEvents: WabunkaExperienceEventListViewModel;
  canReserveEventOptions: WabunkaCandidateEventOption[];
  isSelectedDate: boolean;
  getErrorMessage: (s: string) => string;
  initialUpdateFromExperienceEvent: (experienceEvent: WabunkaExperienceEventViewModel) => void;
  onChangeExperienceEventId: (experienceEventId: string) => void;
  isOvernightStayEvent: boolean;
}

const props = defineProps<Props>();
// ローカルで選択された体験イベントIDを管理
const selectedExperienceEventId = ref(props.reservationFormStep1.experienceEventId);

// 時間が選択された＝体験イベントが選択された
watch(selectedExperienceEventId, (newValue) => {
  if (props.reservationFormStep1.isValidated) {
    return;
  }
  // １日のイベント数は少ないためfind許容
  const selectedEvent = props.candidateEvents.find((event) => event.id === newValue);
  if (selectedEvent == null) {
    // reset experience event form
    props.initialUpdateFromExperienceEvent(createDefaultWabunkaExperienceEventViewModel());
    return;
  }
  props.initialUpdateFromExperienceEvent(selectedEvent);
  // 親コンポーネントと重複するため、GAイベント送信はしない
});
watch(
  () => props.reservationFormStep1.experienceEventId,
  () => {
    // 画面表示に反映する
    selectedExperienceEventId.value = props.reservationFormStep1.experienceEventId;
  },
  { immediate: true },
);
</script>
